// src/components/MonitorPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, RadialBarChart, RadialBar } from "recharts";
import { Download, Gauge, FileText } from "lucide-react";
import * as XLSX from "xlsx";


const excelDate = (d) => (d.getTime() - Date.UTC(1899, 11, 30)) / 86400000;


/* ------------------ Theme helpers ------------------ */
// Tailwind dark mode usually toggles the `dark` class on <html>.
// We watch that so charts react instantly when the theme flips.
function useIsDark() {
  const [isDark, setIsDark] = useState(
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.documentElement;
    const obs = new MutationObserver(() =>
      setIsDark(el.classList.contains("dark"))
    );
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

/* ------------------ Branding colors ------------------ */
const UAS_YELLOW = "#f7d036";
const TRACK_LIGHT = "#e5e7eb"; // gray-200
const TRACK_DARK = "#1f2937"; // gray-800
const ARC_LIGHT = "#111827"; // gray-900 (brand black)
const ARC_DARK = UAS_YELLOW; // brand yellow in dark mode

/* ------------------ Constants ------------------ */
const FORMS_ALL = "ALL";

/* ------------------ Component ------------------ */
export default function MonitorPanel({ forms = [], apiBase, authHeaders }) {
  const isDark = useIsDark();
  const safeForms = Array.isArray(forms) ? forms : [];

  // selection & filters
  const [formId, setFormId] = useState(FORMS_ALL); // default to ALL
  const [sinceDays, setSinceDays] = useState(30);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  // paging
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // data
  const [overview, setOverview] = useState(null);
  const [scores, setScores] = useState(null);
  // meters data for ALL mode (completion per form)
  const [allCompletions, setAllCompletions] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // modals
  const [openAnswers, setOpenAnswers] = useState(null);
  const [openReport, setOpenReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Ensure we never end up with an empty formId (stick to ALL)
  useEffect(() => {
    if (!formId) setFormId(FORMS_ALL);
  }, [safeForms, formId]);

  // ---- URL helper: handles ALL vs specific form ----
  const urlFor = (name, params = {}) => {
    const all = formId === FORMS_ALL;
    let path;

    if (name === "overview") path = all ? "/stats/overview" : `/stats/forms/${formId}/overview`;
    else if (name === "scores") path = all ? "/stats/scores" : `/stats/forms/${formId}/scores`;
    else if (name === "completionByForm") path = all ? "/stats/completion-by-form" : `/stats/forms/${formId}/completion-by-form`;
    else if (name === "respondents") path = all ? "/stats/respondents" : `/stats/forms/${formId}/respondents`;
    else if (name === "report") path = all ? "/stats/report" : `/stats/forms/${formId}/report`;
    else if (name === "export") path = all ? "/stats/export" : `/stats/forms/${formId}/export`;
    else path = name.startsWith("/") ? name : `/${name}`;

    const search = new URLSearchParams(params);
    const qs = search.toString();
    return `${apiBase}${path}${qs ? `?${qs}` : ""}`;
  };


  // ---- fetchers ----
  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await fetch(urlFor("overview"), { headers: authHeaders });
      const data = await res.json();
      if (sinceDays < 30 && Array.isArray(data.series)) {
        const cutoff = new Date(Date.now() - sinceDays * 86400000)
          .toISOString()
          .slice(0, 10);
        data.series = data.series.filter(
          (d) => String(d.day).slice(0, 10) >= cutoff
        );
      }
      setOverview(data);
    } catch (e) {
      console.error(e);
      setOverview(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchScores = async () => {
    try {
      const res = await fetch(urlFor("scores"), { headers: authHeaders });
      setScores(await res.json());
    } catch (e) {
      console.error(e);
      setScores(null);
    }
  };

  // ALL mode: completion % per form
  const fetchCompletionByForm = async () => {
    try {
      const res = await fetch(urlFor("completionByForm"), { headers: authHeaders });
      const data = await res.json();
      setAllCompletions(Array.isArray(data) ? data : (data.items || []));
    } catch (e) {
      console.error(e);
      setAllCompletions(null);
    }
  };

 const fetchRespondents = async () => {
  setLoading(true);
  try {
    // Detect if we want Q&A (single form AND no numeric scores known yet)
    const wantQASummary =
      formId !== FORMS_ALL &&
      !scores?.overall &&
      !(scores?.perQuestion?.length > 0);

    // base params
    const base = {
      page: String(page),
      // if we're aggregating Q&A, pull more rows in one shot
      limit: wantQASummary ? "1000" : String(limit),
      q,
      status: status === "all" ? "" : status,
    };

    // ask backend to include answers for single-form view
    const params = formId === FORMS_ALL ? base : { ...base, includeAnswers: "1" };

    // primary attempt
    let res = await fetch(urlFor("respondents", params), { headers: authHeaders });
    let data = await res.json();

    // Fallbacks if the API uses different knobs for including answers
    const noAnswers =
      (data.rows || []).length > 0 &&
      !(data.rows || []).some(r => r.answers && Object.keys(r.answers).length);

    if (formId !== FORMS_ALL && noAnswers) {
      // try ?fields=answers
      const p1 = { ...base, fields: "answers" };
      const r1 = await fetch(urlFor("respondents", p1), { headers: authHeaders });
      const d1 = await r1.json();

      if ((d1.rows || []).some(r => r.answers && Object.keys(r.answers).length)) {
        data = d1;
      } else {
        // try ?answers=1
        const p2 = { ...base, answers: "1" };
        const r2 = await fetch(urlFor("respondents", p2), { headers: authHeaders });
        const d2 = await r2.json();
        if ((d2.rows || []).some(r => r.answers && Object.keys(r.answers).length)) {
          data = d2;
        }
      }
    }

    setRows(data.rows || []);
    setTotal(data.total || 0);
  } catch (e) {
    console.error(e);
    setRows([]);
    setTotal(0);
  } finally {
    setLoading(false);
  }
};


  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const res = await fetch(urlFor("report"), { headers: authHeaders });
      const data = await res.json();
      setOpenReport(data);
    } catch (e) {
      console.error(e);
      setOpenReport({ error: "Failed to load report" });
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    if (formId === FORMS_ALL) {
      fetchCompletionByForm();
      setScores(null);
    } else {
      fetchScores();
      setAllCompletions(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, sinceDays]);

  useEffect(() => {
    fetchRespondents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, page, q, status]);

  // ---- derived ----
  const totals = {
    total: overview?.total ?? 0,
    completed: overview?.completed ?? 0,
    pending: overview?.pending ?? 0,
    completionRate: (overview?.completionRate ?? 0) * 100,
  };

  const scoreScale = useMemo(() => {
    if (formId === FORMS_ALL && allCompletions) return 100; // percent scale for ALL
    const maxQ = Math.max(
      5,
      ...(scores?.perQuestion || []).map((q) => q.max || 0),
      scores?.overall?.avg ? Math.ceil(scores.overall.avg) : 0
    );
    return maxQ || 5;
  }, [scores, formId, allCompletions]);

  const gaugeCards = useMemo(() => {
    if (formId === FORMS_ALL && Array.isArray(allCompletions)) {
      return allCompletions.map(f => ({
        label: f.title || f.formTitle || (f.formId ?? "(untitled)"),
        raw: (f.completionRate ?? 0) * 100,
        pct: Math.max(0, Math.min(100, (f.completionRate ?? 0) * 100)),
        n: f.total ?? f.count ?? 0
      }));
    }
    if (!scores) return [];
    const norm = (v) => (scoreScale > 0 ? Math.max(0, Math.min(100, (v / scoreScale) * 100)) : 0);
    const cards = [];
    if (scores.overall?.avg != null) {
      cards.push({ label: "Overall", raw: scores.overall.avg, pct: norm(scores.overall.avg), n: scores.overall.count });
    }
    (scores.perQuestion || []).forEach((q) => {
      cards.push({ label: q.question, raw: q.avg, pct: norm(q.avg), n: q.n });
    });
    return cards;
  }, [scores, scoreScale, formId, allCompletions]);

  const pages = Math.max(1, Math.ceil(total / limit));

  // ---- CSV exporter ----
const toExcel = async () => {
  try {
    const params = { q, status: status === "all" ? "" : status };
    const res = await fetch(urlFor("export", params), { headers: authHeaders });
    if (!res.ok) throw new Error(`Export failed: ${res.status}`);

    // If backend already streams CSV, we still allow download (unchanged)
    const contentType = res.headers.get("content-type") || "";
// ---------- Path B: CSV fallback -> strip PII columns -> save XLSX ----------
if (contentType.includes("text/csv")) {
  const csvText = await res.text();

  // Parse CSV -> arrays of rows
  const wbCSV = XLSX.read(csvText, { type: "string" });
  const wsCSV = wbCSV.Sheets[wbCSV.SheetNames[0]];
  const aoa = XLSX.utils.sheet_to_json(wsCSV, { header: 1, raw: false });
  if (!aoa.length) {
    alert("No data to export");
    return;
  }

  // Columns to exclude (case-insensitive)
  const EXCLUDE = new Set([
    "recipientemail",        // email
    "respondentname",        // name
    "respondentdepartment",  // department
    "name",
    "email",
    "department",
  ]);

  // Keep only non-excluded columns
  const headerRow = aoa[0].map((h) => String(h || "").trim());
  const keepIdx = headerRow
    .map((h, i) => ({ i, h: h.toLowerCase() }))
    .filter(({ h }) => !EXCLUDE.has(h))
    .map(({ i }) => i);

  const cleaned = aoa.map((row) => keepIdx.map((i) => row[i]));

  // Build worksheet/workbook
  const ws = XLSX.utils.aoa_to_sheet(cleaned);
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft", state: "frozen" };
  ws["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: cleaned.length - 1, c: (cleaned[0]?.length || 1) - 1 },
    }),
  };
  ws["!cols"] = (cleaned[0] || []).map((h) => ({ wch: Math.max(12, String(h).length + 2) }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, formId === FORMS_ALL ? "All Forms" : "Survey");

  const filename = `survey-export-${formId === FORMS_ALL ? "ALL" : formId}-${new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/[:T]/g, "")}.xlsx`;
  XLSX.writeFile(wb, filename);
  return;
}


    // Otherwise we assume JSON payload and build a proper .xlsx
    const payload = await res.json();
    const docs = Array.isArray(payload) ? payload : (payload.items || payload.rows || []);
    if (!docs.length) {
      alert("No data to export");
      return;
    }

    // Dynamic answer columns
    const answerKeys = Array.from(new Set(docs.flatMap(d => Object.keys(d.answers || {})))).sort();

    // Fixed header order: Removed respondentName and respondentDepartment
    const header = [
      "_id", "formId", "title", 
      "status", "sentAt", "completedAt", "minutesToComplete", "sentBy",
      ...answerKeys.map(k => `ans:${k}`)
    ];

    // Build 2D array for the sheet (A1 style)
    const rows2D = [header];

    for (const d of docs) {
      // AFTER  ✅  write numeric Excel serials into the sheet data
      const sentSerial = d.sentAt ? excelDate(new Date(d.sentAt)) : "";
      const compSerial = d.completedAt ? excelDate(new Date(d.completedAt)) : "";
      const mins = (d.completedAt && d.sentAt)
        ? ((new Date(d.completedAt) - new Date(d.sentAt)) / (1000 * 60))
        : "";

      const base = [
        d._id || d.assignmentId || "",
        d.formId || "",
        d.title || "",
        d.status || "",
        sentSerial,
        compSerial,
        mins === "" ? "" : Number(mins),
        d.sentBy || "",
      ];

      const ans = answerKeys.map(k => {
        const v = (d.answers || {})[k];
        return v == null ? "" : (typeof v === "object" ? JSON.stringify(v) : String(v));
      });

      rows2D.push([...base, ...ans]);
    }

    // Create sheet and apply formats
    const ws = XLSX.utils.aoa_to_sheet(rows2D);

    // Freeze header row
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft", state: "frozen" };

    // Auto filter on the header
    const lastColIdx = header.length - 1;
    ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows2D.length - 1, c: lastColIdx } }) };

    // Column widths
    ws["!cols"] = header.map(h => ({ wch: Math.max(12, String(h).length + 2) }));

    // ----- Force real Excel types + formats (single loop, no instanceof Date) -----
    const SENT_AT_COL = header.indexOf("sentAt");
    const COMPLETED_AT_COL = header.indexOf("completedAt");
    const MINUTES_COL = header.indexOf("minutesToComplete");

    for (let r = 1; r < rows2D.length; r++) {
      if (SENT_AT_COL >= 0) {
        const a = XLSX.utils.encode_cell({ r, c: SENT_AT_COL });
        const cell = ws[a];
        if (cell && typeof cell.v === "number") { cell.t = "n"; cell.z = "yyyy-mm-dd hh:mm:ss"; }
      }
      if (COMPLETED_AT_COL >= 0) {
        const a = XLSX.utils.encode_cell({ r, c: COMPLETED_AT_COL });
        const cell = ws[a];
        if (cell && typeof cell.v === "number") { cell.t = "n"; cell.z = "yyyy-mm-dd hh:mm:ss"; }
      }
      if (MINUTES_COL >= 0) {
        const a = XLSX.utils.encode_cell({ r, c: MINUTES_COL });
        const cell = ws[a];
        if (cell && typeof cell.v === "number") { cell.t = "n"; cell.z = "0.00"; }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, formId === FORMS_ALL ? "All Forms" : "Survey");

    const filename = `survey-export-${formId === FORMS_ALL ? "ALL" : formId}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.xlsx`;
    XLSX.writeFile(wb, filename);
  } catch (e) {
    console.error("Export failed", e);
    alert("Export failed. Check console for details.");
  }
};


  // Q&A summary (for non-rating forms) – aggregate answers from respondents
// Q&A summary (for non-rating forms) – aggregate answers from respondents
const qaSummary = useMemo(() => {

  // Only for a single form; if numeric scores exist we keep the gauge view
if (formId === FORMS_ALL) return null;

const hasNumeric =
  (Array.isArray(scores?.perQuestion) && scores.perQuestion.length > 0) ||
  (typeof scores?.overall?.avg === "number" && (scores?.overall?.count ?? 0) > 0);

if (hasNumeric) return null;


  // ✅ only take completed rows that actually carry answers
  const filled = (rows || []).filter(r =>
    r?.status === "completed" &&
    r?.answers &&
    Object.keys(r.answers).length > 0
  );

  if (!filled.length) return [];

  const acc = new Map(); // question -> Map(answer -> count)

  filled.forEach(r => {
    Object.entries(r.answers).forEach(([q, v]) => {
      const question = String(q).trim();
      const value =
        v == null ? "—" :
        (typeof v === "object" ? JSON.stringify(v) : String(v));

      if (!acc.has(question)) acc.set(question, new Map());
      const m = acc.get(question);
      m.set(value, (m.get(value) || 0) + 1);
    });
  });

  return Array.from(acc.entries()).map(([question, amap]) => ({
    question,
    total: Array.from(amap.values()).reduce((a, b) => a + b, 0),
    answers: Array.from(amap.entries()).map(([value, count]) => ({ value, count })),
  }));
}, [formId, scores, rows]);




  return (
    <div className="space-y-6">
      <Card
        title="Control Window"
        rightSlot={
          <div className="flex gap-2">
            <Btn onClick={fetchReport} disabled={reportLoading} icon={<FileText className="w-4 h-4" />}>
              {reportLoading ? "Analyzing…" : "Analyze Report"}
            </Btn>
            <Btn tone="neutral" onClick={toExcel} icon={<Download className="w-4 h-4" />}>
              Export Excel
            </Btn>

          </div>
        }
      >
        {/* Controls */}
        <Controls
          forms={safeForms}
          formId={formId}
          setFormId={setFormId}
          sinceDays={sinceDays}
          setSinceDays={setSinceDays}
          status={status}
          setStatus={setStatus}
          q={q}
          setQ={setQ}
          setPage={setPage}
        />

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <Kpi label="Total Sent" value={totals.total} />
          <Kpi label="Completed" value={totals.completed} />
          <Kpi label="Pending" value={totals.pending} />
          <Kpi label="Completion Rate" value={`${totals.completionRate.toFixed(1)}%`} />
        </div>

 
{/* Scores */}
<div className="mt-8">
  {/* Header */}
  <div className="mb-3 flex items-center gap-2">
    <Gauge className="h-5 w-5" aria-hidden="true" />
    <h3 className="text-sm font-semibold">
      {formId === FORMS_ALL ? "Completion by Form"
        : (qaSummary?.length ? "Question & Answer Summary" : "Survey Scores")}
    </h3>
    {!qaSummary?.length && <span className="ml-2 text-xs text-gray-500">Scale: {scoreScale}</span>}
  </div>

  {/* Content */}
  {formId === FORMS_ALL ? (
    gaugeCards.length ? (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {gaugeCards.map((c, i) => (
          <GaugeCard
            key={i}
            {...c}
            max={scoreScale}
            isDark={isDark}
            className="min-h-[240px]"
          />
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500 dark:text-gray-300">No numeric answers yet.</p>
    )
  ) : qaSummary?.length ? (
    <QATable summary={qaSummary} />
  ) : (
    <div className="grid gap-6 md:grid-cols-[1fr_2fr] items-stretch">
      <GaugeCard
        label="Overall"
        raw={scores?.overall?.avg}
        pct={scoreScale ? Math.max(0, Math.min(100, (scores?.overall?.avg / scoreScale) * 100)) : 0}
        n={scores?.overall?.count}
        max={scoreScale}
        isDark={isDark}
        fill
        className="h-full min-h-[360px]"
      />
      <QuestionScoresTable items={scores?.perQuestion || []} />
    </div>
  )}
</div>





        {/* Respondents */}
        <div className="mt-6">
          <RespondentsTable
            rows={rows}
            loading={loading}
            isAll={formId === FORMS_ALL}
            formTitle={overview?.title || ""}
          />
        </div>

        {/* Pagination */}
        <div className="p-3 flex items-center justify-end gap-2">
          <BtnSmall tone="neutral" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</BtnSmall>
          <div className="text-sm text-gray-600 dark:text-gray-300">Page {page}</div>
          <BtnSmall tone="neutral" disabled={page * limit >= total} onClick={() => setPage((p) => p + 1)}>Next</BtnSmall>
        </div>
      </Card>

      {/* Answers modal */}
      {openAnswers && (
        <Modal title={`Answers — ${openAnswers.recipientEmail}`} onClose={() => setOpenAnswers(null)}>
          <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(openAnswers.answers, null, 2)}</pre>
        </Modal>
      )}

      {/* Report modal */}
      {openReport && (
        <Modal title="Comprehensive Report" onClose={() => setOpenReport(null)}>
          {openReport.error ? (
            <div className="text-red-500 dark:text-red-300">{openReport.error}</div>
          ) : (
            <pre className="text-sm whitespace-pre-wrap">{openReport.analysis}</pre>
          )}
        </Modal>
      )}
    </div>
  );
}


function QATable({ summary = [] }) {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAnswer = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      {/* Header Section */}
      <div className="px-4 py-3 bg-black text-white dark:bg-uasYellow dark:text-black border-b border-black/80 dark:border-yellow-500/40 flex items-center justify-between">
        <h3 className="text-sm font-bold">Question & Answer Summary</h3>
      </div>

      {/* Body Section */}
      <div className="p-4 space-y-4">
        {summary.length > 0 ? (
          summary.map((q, idx) => (
            <div key={`${q.question}-${idx}`} className="space-y-4">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                {/* Question Title with Toggle Button */}
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => toggleAnswer(idx)}
                >
                  <h3 className="font-semibold text-lg dark:text-white">{q.question}</h3>
                  <span className="text-gray-500 dark:text-gray-300">
                    {openIndex === idx ? "−" : "+"}
                  </span>
                </div>

                {/* Answer Accordion */}
                {openIndex === idx && (
                  <div className="mt-2 space-y-2">
                    {q.answers.sort((a, b) => b.count - a.count).map((a, idxAnswer) => (
                      <div
                        key={`${q.question}-${a.value}-${idxAnswer}`}
                        className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-md p-3 shadow-sm border border-gray-200 dark:border-gray-700"
                      >
                        {/* Answer */}
                        <p className="text-sm text-gray-700 dark:text-gray-300">{a.value}</p>

                        {/* Count */}
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{a.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="p-4 text-center text-gray-500 dark:text-gray-300">
            No answers found for this form.
          </div>
        )}
      </div>
    </div>
  );
}



/* ================= Reusable UI (brand) ================= */
function Card({ title, rightSlot, children }) {
  return (
    <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div className="px-4 py-3 bg-black text-white dark:bg-uasYellow dark:text-black border-b border-black/80 dark:border-yellow-500/40 flex items-center justify-between">
        <h3 className="text-sm font-bold">{title}</h3>
        {rightSlot}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Btn({ children, onClick, disabled, className = "", tone = "accent", icon }) {
  const wrap =
    tone === "neutral"
      ? "from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 text-gray-900 dark:text-white"
      : "from-black to-gray-800 dark:from-uasYellow dark:to-yellow-500 text-white dark:text-black";
  const innerBg = tone === "neutral" ? "bg-white/60 dark:bg-transparent" : "bg-black dark:bg-transparent";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative inline-flex items-center justify-center overflow-hidden p-0.5 text-sm font-medium rounded-lg transition disabled:opacity-60 bg-gradient-to-br ${wrap} ${className}`}
    >
      <span className={`relative px-3 py-2 rounded-md border border-black/20 dark:border-transparent flex items-center gap-2 ${innerBg}`}>
        {icon}
        {children}
      </span>
    </button>
  );
}

function BtnSmall({ children, onClick, disabled, tone = "neutral" }) {
  const base = "px-3 py-1.5 rounded-md text-sm border transition disabled:opacity-60";
  const styles =
    tone === "neutral"
      ? "bg-gray-100 hover:bg-gray-200 border-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600 text-gray-900 dark:text-gray-100"
      : "bg-black text-white dark:bg-uasYellow dark:text-black border-black/80";
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${styles}`}>
      {children}
    </button>
  );
}

/* =============== Controls, KPI, Table =============== */
function Controls({ forms, formId, setFormId, sinceDays, setSinceDays, status, setStatus, q, setQ, setPage }) {
  const list = Array.isArray(forms) ? forms : [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <Select label="Form" value={formId} onChange={(e) => { setFormId(e.target.value); setPage(1); }}>
        <option value={FORMS_ALL}>All Forms</option>
        {list.map((f) => (
          <option key={f._id} value={f._id}>
            {f.title}
          </option>
        ))}
      </Select>
      <Select label="Since" value={sinceDays} onChange={(e) => setSinceDays(parseInt(e.target.value, 10))}>
        {[7, 14, 30, 60, 90, 180, 365].map((d) => (
          <option key={d} value={d}>
            {d} days
          </option>
        ))}
      </Select>
      <Select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
        <option value="all">All</option>
        <option value="pending">Pending</option>
        <option value="completed">Completed</option>
      </Select>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">Search</label>
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
          placeholder="Email…"
        />
      </div>
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">{label}</label>
      <select
        {...props}
        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
      >
        {children}
      </select>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg border dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</div>
      <div className="text-2xl font-semibold text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}

/* =============== Gauges =============== */
function GaugeGrid({ cards, max, isDark }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {cards.map((c, i) => (
        <GaugeCard key={i} {...c} max={max} isDark={isDark} />
      ))}
    </div>
  );
}


// before: function GaugeCard({ label, pct, raw, max, n, isDark, className = "" }) {
function GaugeCard({
  label,
  pct,
  raw,
  max,
  n,
  isDark,
  className = "",
  fill = false,              // ⬅️ new prop (default false)
}) {
  const data = [{ name: label, v: Math.max(0, Math.min(100, pct ?? 0)) }];
  const arcColor = isDark ? ARC_DARK : ARC_LIGHT;
  const trackColor = isDark ? TRACK_DARK : TRACK_LIGHT;

  // use fixed height for normal (ALL forms) cards, stretch only when asked
  const chartHeightClass = fill ? "h-full" : "h-40 md:h-48";   // ⬅️ changed

  return (
    <div className={`flex flex-col rounded-xl border dark:border-gray-700 p-4 bg-white dark:bg-gray-900 ${className}`}>
      <div className="mb-2 truncate text-sm text-gray-700 dark:text-gray-300">{label}</div>

      <div className={`flex-1 ${chartHeightClass}`}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            data={data}
            cx="50%"
            cy="50%"
            innerRadius="55%"
            outerRadius="105%"
            startAngle={180}
            endAngle={0}
            barSize={12}
          >
            <RadialBar dataKey="v" data={[{ v: 100 }]} fill={trackColor} isAnimationActive={false} />
            <RadialBar dataKey="v" cornerRadius={10} fill={arcColor} background={false} isAnimationActive={false} />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 text-center">
        <div className="text-xl font-semibold text-gray-900 dark:text-white">
          {raw?.toFixed(2) ?? "—"} <span className="text-sm text-gray-500">/ {max}</span>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">n={n}</div>
      </div>
    </div>
  );
}



function QuestionScoresTable({ items = [] }) {
  const HEAD = ["Question", "Average"];

  return (
    <div className="overflow-auto border rounded-lg border-gray-200 dark:border-gray-700">
      <table className="w-full text-sm">
        <thead className="bg-black text-white dark:bg-uasYellow dark:text-black">
          <tr>
            {HEAD.map(h => <Th key={h}>{h}</Th>)}
          </tr>
        </thead>

        <tbody>
          {items.length ? (
            items.map((q, i) => (
              <tr
                key={q.question || i}
                className="border-t border-gray-200 dark:border-gray-700 odd:bg-gray-50 dark:odd:bg-gray-800/30"
              >
                {[
                  <Td key="q">{q.question || q.name || `Q${i + 1}`}</Td>,
                  <Td key="avg" className="whitespace-nowrap">
                    {q.avg != null ? Number(q.avg).toFixed(2) : "—"}
                  </Td>,
                ]}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={HEAD.length} className="p-4 text-center text-gray-500 dark:text-gray-300">
                No question-level scores yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


/* =============== Table =============== */
function RespondentsTable({ rows, loading, isAll, formTitle }) {
  const [fStatus, setFStatus] = React.useState("all");
  const [fTitle, setFTitle] = React.useState("");
  const [fDept, setFDept] = React.useState("");   // 👈 NEW state

  // Unique department list
  const deptOptions = React.useMemo(() => {
    const set = new Set((rows || []).map(r =>
      r.respondentDepartment || r.employee?.department || ""
    ).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);
    const HEADERS = ["Email","Name","Department","Status","Sent","Completed","Form Title"];


  // Build title options from current page rows (ALL only)
  const titleOptions = React.useMemo(() => {
    if (!isAll) return [];
    const set = new Set((rows || []).map(r => r.title).filter(Boolean));
    return Array.from(set).sort();
  }, [rows, isAll]);

  // Apply client-side filters
  const viewRows = React.useMemo(() => {
    return (rows || []).filter(r => {
      const okStatus = fStatus === "all" ? true : r.status === fStatus;
      const okTitle = !isAll ? true : (fTitle ? (r.title || "") === fTitle : true);
      const dept = r.respondentDepartment || r.employee?.department || "";
      const okDept = fDept ? dept === fDept : true;
      return okStatus && okTitle && okDept;
    });
  }, [rows, fStatus, fTitle, fDept, isAll]);

  return (
    <div className="overflow-auto border rounded-lg border-gray-200 dark:border-gray-700">
      {/* Inline filters */}
      <div className="flex flex-wrap gap-3 p-3 items-end bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700">
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
            Status
          </label>
          <select
            className="px-2 py-1.5 rounded-md border bg-white dark:bg-gray-900 text-sm
                       border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
            value={fStatus}
            onChange={(e) => setFStatus(e.target.value)}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
            Department
          </label>
          <select
            className="px-2 py-1.5 rounded-md border bg-white dark:bg-gray-900 text-sm
               border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 min-w-[12rem]"
            value={fDept}
            onChange={(e) => setFDept(e.target.value)}
          >
            <option value="">All Departments</option>
            {deptOptions.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>


        {isAll && (
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
              Form Title
            </label>
            <select
              className="px-2 py-1.5 rounded-md border bg-white dark:bg-gray-900 text-sm
                         border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 min-w-[12rem]"
              value={fTitle}
              onChange={(e) => setFTitle(e.target.value)}
            >
              <option value="">All Forms</option>
              {titleOptions.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

          </div>
        )}
      </div>



      <table className="w-full text-sm">

<thead className="bg-black text-white dark:bg-uasYellow dark:text-black">
  <tr>
    {HEADERS.map(h => <Th key={h}>{h}</Th>)}
  </tr>
</thead>

<tbody>
  {viewRows.map(r => {
    const name = r.respondentName || r.employee?.name || "—";
    const dept = r.respondentDepartment || r.employee?.department || "—";
    return (
      <tr
        key={r.assignmentId || `${r.recipientEmail}-${r.sentAt}`}
        className="border-t border-gray-200 dark:border-gray-700 odd:bg-gray-50 dark:odd:bg-gray-800/30"
      >
        {[
          <Td key="email" className="whitespace-nowrap">{r.recipientEmail}</Td>,
          <Td key="name"  className="whitespace-nowrap">{name}</Td>,
          <Td key="dept"  className="whitespace-nowrap">{dept}</Td>,
          <Td key="status">
            <span className={`px-2 py-0.5 rounded-full text-xs border ${
              r.status === "completed"
                ? "bg-green-100 text-green-800 border-green-200"
                : "bg-yellow-100 text-yellow-800 border-yellow-200"
            }`}>
              {r.status}
            </span>
          </Td>,
          <Td key="sent">{r.sentAt ? new Date(r.sentAt).toLocaleString() : "—"}</Td>,
          <Td key="done">{r.completedAt ? new Date(r.completedAt).toLocaleString() : "—"}</Td>,
          <Td key="title">{isAll ? (r.title || "—") : (formTitle || "—")}</Td>,
        ]}
      </tr>
    );
  })}
</tbody>

      </table>
    </div>
  );
}


/* =============== Modal =============== */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-laryzeDarkGray rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 bg-black text-white dark:bg-uasYellow dark:text-black border-b border-black/80 dark:border-yellow-500/40 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/10 dark:hover:bg-black/10">
            ✕
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

const Th = (p) => <th className="text-left p-2 font-semibold">{p.children}</th>;
const Td = ({ children, className = "" }) => (
  <td className={`p-2 align-top text-gray-900 dark:text-gray-100 ${className}`}>{children}</td>
);
